import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, JoyKitThemeProvider, useDisclosure } from '@withjoy/joykit';

import { useCarRates } from './hooks/useCarRates';
import { useReserveCar } from './hooks/useReserveCar';
import { useCarFilters } from './hooks/useCarFilters';
import { useHistory, useLocation } from '@shared/core';
import { useMount } from '@shared/utils/hooks/useMount';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { EmptyState } from './components/EmptyState/EmptyState';
import { ErrorState } from './components/ErrorState/ErrorState';
import { BottomSheet } from '@apps/stays/components/BottomSheet';
import { useCarRentalCurrency } from './hooks/useCarRentalCurrency';
import { DEFAULT_CAR_SORT_OPTION } from './CarRentalConsole.constants';
import { useCarSearchParamsState } from './hooks/useCarSearchParamsState';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import { CarFiltersPanel } from './components/FiltersPanel/CarFiltersPanel';
import { useCarRentalConsoleTelemetry } from './CarRentalConsole.telemetry';
import { ProgressiveLoader } from '@apps/stays/components/ProgressiveLoader';
import { CarListingsPanel } from './components/ListingsPanel/CarListingsPanel';
import { useGeocodeCarSearchLocation } from './hooks/useGeocodeCarSearchLocation';
import { CarListingsSkeleton } from './components/ListingsPanel/CarListingsSkeleton';
import type { UseCarSearchParamsStateReturn } from './hooks/useCarSearchParamsState';
import type { CarFiltersPanelProps } from './components/FiltersPanel/CarFiltersPanel.types';
import { CarMobileSearch, CarSearchFormRow, CarSearchHeader } from './components/Header/Header';
import { useCarRentalConsoleHealth, wrapWithCarRentalConsoleProvider } from './CarRentalConsole.health';
import { getHomogeneousRatesCurrency, isValidCarLocation, sortCarVehicles } from './CarRentalConsole.utils';
import type { CarRatesSortDto, CarRentalConsoleProps, CarSearchState, CarVehicle } from './CarRentalConsole.types';

type RootProps = Readonly<{
  eventName: string;
  eventHandle: string;
  state: CarSearchState;
  onSearchSubmit: UseCarSearchParamsStateReturn['handleSearchSubmit'];
}>;

const Root = ({ state, eventName, eventHandle, onSearchSubmit }: RootProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  const { isOpen: isFilterPanelOpen, onOpen: onFilterPanelOpen, onClose: onFilterPanelClose } = useDisclosure();

  const history = useHistory();
  const location = useLocation();
  const telemetry = useCarRentalConsoleTelemetry();

  const { onLoadComplete, onLoadFailure } = useCarRentalConsoleHealth();
  const [sortOption, setSortOption] = useState<CarRatesSortDto>(DEFAULT_CAR_SORT_OPTION);

  const { vehicles, loading: ratesLoading, isInitialLoading: ratesInitialLoading, error } = useCarRates(state);
  const { isGeocoding, pendingDestination } = useGeocodeCarSearchLocation();
  const loading = ratesLoading || isGeocoding;
  const isInitialLoading = ratesInitialLoading || isGeocoding;

  const { reserve, reservingOfferToken } = useReserveCar(state);

  const viewCarDetails = useCallback(
    (vehicle: CarVehicle) => {
      telemetry.detailsViewClicked({ name: vehicle.description, offerToken: vehicle.offerToken });
      const params = new URLSearchParams(location.search);
      params.set('offerToken', vehicle.offerToken);
      history.push(`/${eventHandle}/car/details?${params.toString()}`);
    },
    [history, location.search, eventHandle, telemetry],
  );

  useEffect(() => {
    if (loading) return;
    if (error) onLoadFailure(error, { state, eventHandle });
    else onLoadComplete();
  }, [loading, error, onLoadComplete, onLoadFailure, state, eventHandle]);

  useEffect(() => {
    if (!isMobileOrTablet) {
      onFilterPanelClose();
      onSearchClose();
    }
  }, [isMobileOrTablet, onFilterPanelClose, onSearchClose]);

  const {
    filters,
    priceRange,
    resetFilters,
    updateFilters,
    carNameSearch,
    carTypeCounts,
    companyCounts,
    fuelTypeCounts,
    driveTypeCounts,
    dynamicPriceMax,
    updatePriceRange,
    setCarNameSearch,
    filteredVehicles,
    supportedCarTypes,
    supportedCompanies,
    transmissionCounts,
    supportedFuelTypes,
    supportedDriveTypes,
    passengerBucketCounts,
    supportedTransmissions,
    cancellationTypeCounts,
    supportedCancellationTypes,
  } = useCarFilters(vehicles);

  const sortedVehicles = useMemo(() => sortCarVehicles(filteredVehicles, sortOption, state.pickUpLocation), [filteredVehicles, sortOption, state.pickUpLocation]);
  const updateSort = useCallback((nextSort: CarRatesSortDto) => setSortOption(nextSort), []);
  const resetFiltersAndSort = useCallback(() => {
    resetFilters();
    setSortOption(DEFAULT_CAR_SORT_OPTION);
  }, [resetFilters]);

  const { currency: currencyCode } = useCarRentalCurrency();
  const navigatorLanguage = useMemo(() => readNavigatorLanguage(), []);

  const filterCurrencyCode = useMemo(() => getHomogeneousRatesCurrency(vehicles) ?? currencyCode, [vehicles, currencyCode]);

  const filtersPanelProps: CarFiltersPanelProps = {
    filters,
    priceRange,
    sortOption,
    carNameSearch,
    carTypeCounts,
    companyCounts,
    fuelTypeCounts,
    driveTypeCounts,
    dynamicPriceMax,
    navigatorLanguage,
    supportedCarTypes,
    supportedCompanies,
    transmissionCounts,
    supportedFuelTypes,
    supportedDriveTypes,
    passengerBucketCounts,
    supportedTransmissions,
    cancellationTypeCounts,
    supportedCancellationTypes,
    onSortChange: updateSort,
    onFiltersChange: updateFilters,
    currencyCode: filterCurrencyCode,
    onPriceRangeChange: updatePriceRange,
    onCarNameSearchChange: setCarNameSearch,
  };

  return (
    <Flex
      width="100%"
      height="100dvh"
      maxWidth="100%"
      flexDirection="column"
      overflow={isMobileOrTablet ? 'auto' : 'hidden'}
    >
      <CarSearchHeader
        state={state}
        eventHandle={eventHandle}
        onSearchOpen={onSearchOpen}
        onFilterPanelOpen={onFilterPanelOpen}
        pendingDestination={pendingDestination}
      />
      <CarSearchFormRow
        state={state}
        isGeocoding={isGeocoding}
        onSearchSubmit={onSearchSubmit}
        pendingDestination={pendingDestination}
      />
      <ProgressiveLoader isLoading={loading} />
      {isInitialLoading ? (
        <CarListingsSkeleton />
      ) : error && vehicles.length === 0 ? (
        <ErrorState />
      ) : (
        <Flex
          flex={1}
          minHeight={isMobileOrTablet ? '400px' : 0}
          flexDirection={{ _: 'column', md: 'row' }}
          overflow={isMobileOrTablet ? undefined : 'hidden'}
        >
          {!isMobileOrTablet && (
            <Flex
              height="100%"
              flexShrink={0}
              overflowY="auto"
            >
              <CarFiltersPanel
                {...filtersPanelProps}
                eventName={eventName}
              />
            </Flex>
          )}
          <Flex
            flex={1}
            minWidth={0}
            height={isMobileOrTablet ? 'auto' : '100%'}
            overflowY={isMobileOrTablet ? undefined : 'auto'}
          >
            {sortedVehicles.length > 0 ? (
              <CarListingsPanel
                onReserve={reserve}
                sortOption={sortOption}
                onSortChange={updateSort}
                vehicles={sortedVehicles}
                onViewDetails={viewCarDetails}
                navigatorLanguage={navigatorLanguage}
                reservingOfferToken={reservingOfferToken}
                searchedPickUpLocation={state.pickUpLocation}
                searchedDropOffLocation={state.dropOffLocation}
              />
            ) : (
              <EmptyState
                onReset={resetFilters}
                showResetButton={vehicles.length > 0}
                hasSearchLocation={isValidCarLocation(state.pickUpLocation)}
              />
            )}
          </Flex>
        </Flex>
      )}
      {isMobileOrTablet && (
        <>
          <BottomSheet
            isOpen={isFilterPanelOpen}
            onClose={onFilterPanelClose}
          >
            <CarFiltersPanel
              {...filtersPanelProps}
              onClose={onFilterPanelClose}
              onReset={resetFiltersAndSort}
            />
          </BottomSheet>
          <BottomSheet
            isOpen={isSearchOpen}
            onClose={onSearchClose}
          >
            <CarMobileSearch
              state={state}
              onClose={onSearchClose}
              isGeocoding={isGeocoding}
              onSearchSubmit={onSearchSubmit}
              pendingDestination={pendingDestination}
            />
          </BottomSheet>
        </>
      )}
    </Flex>
  );
};

export const CarRentalConsole = ({ eventHandle, eventName }: CarRentalConsoleProps) => {
  const { state, handleSearchSubmit } = useCarSearchParamsState();
  const { pageViewed } = useCarRentalConsoleTelemetry();

  useMount(() => {
    pageViewed();
  });

  return (
    <JoyKitThemeProvider>
      <SkeletonGroup
        width="100%"
        isReady={true}
        display="flex"
        height="100dvh"
        maxWidth="100%"
        flexDirection="column"
        placeholder={<CarSearchHeader.Skeleton />}
      >
        <Root
          state={state}
          eventName={eventName}
          eventHandle={eventHandle}
          onSearchSubmit={handleSearchSubmit}
        />
      </SkeletonGroup>
    </JoyKitThemeProvider>
  );
};

export const CarRentalConsoleWithHealth = wrapWithCarRentalConsoleProvider(CarRentalConsole);
